import { Box, Stack } from '@mui/material'
import { useYardStores } from '@yard/AppProvider'
import { getBlockCapacity } from '@yard/utils/allocation-rule-capacity.util'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { NodeProps } from 'reactflow'
import { buildBayGaps, edgeAt } from '../../helpers/yard-view.utils'
import { YardBlockWithOccupanciesAndActions } from '../../models/yard-view.model'
import { YardBlockEditSectionDialogType } from '../../stores/nodes/yard-block.view-store'
import { BlockAccessPointBars } from '../atoms/BlockAccessPointBars'
import { YardBlockBayHeader } from '../atoms/YardBlockBayHeader'
import { YardBlockRowHeader } from '../atoms/YardBlockRowHeader'
import { YardBlockSlots } from '../atoms/YardBlockSlots'
import { FocusWrapper } from '../molecules/FocusWrapper'
import { RotatableNodeWrapper } from '../molecules/RotatableNodeWrapper'
import { YardBlockHeader } from '../molecules/YardBlockHeader'

export const YardBlockNode = observer(({ data }: NodeProps<YardBlockWithOccupanciesAndActions>) => {
  const {
    id,
    name,
    maxBays,
    maxRows,
    maxTiers,
    description,
    equipmentTypes,
    focused,
    showNodeHeaders,
    bayNames,
    rowNames,
    occupancies,
    allocationRules,
    selectedAllocationRuleIndex,
    isEditable,
    showAllocationRulesPreview,
    colorScheme,
    selectedSchemeValues,
    layoutPosition,
    configAreas,
    stackConfigArea,
    hasOccupancies,
    onFocus,
    onEdit,
    onDelete,
    onDoubleClick,
    onRotate,
    onRenameBay,
    onRenameRow,
    onInsertBayLeft,
    onInsertBayRight,
    onInsertRowUp,
    onInsertRowDown,
    onDeleteBay,
    onDeleteRow,
    onBlockConfigArea,
    onDeleteConfigArea,
    onAddReeferPlugs,
    onAddSpaceBetweenBays,
    onDeleteSpaceBetweenBays,
    onAdjustMaxTier,
    onAddAccessPoint,
    onEditAccessPoint,
    reeferConfigAreaByBay,
    spaceBetweenBaysByEdge,
  } = data

  const { occupancyStore, capacitySettingsStore } = useYardStores()
  const blockWarningThreshold = capacitySettingsStore.blockWarningThreshold
  const { isOvercapacity, isWarning, utilizationPct, consumedTeu, totalTeu } = getBlockCapacity(
    data,
    occupancyStore.occupanciesByBlock[id] ?? [],
    blockWarningThreshold,
  )

  const bayGaps = useMemo(() => buildBayGaps(configAreas, maxBays), [configAreas, maxBays])

  const hasLeadingGap = !!edgeAt(bayGaps, 0)
  const hasTrailingGap = !!edgeAt(bayGaps, maxBays)
  const showTopLeftCornerCell = !focused && !showNodeHeaders

  return (
    <RotatableNodeWrapper
      rotation={layoutPosition.rotation}
      enabled={focused && isEditable}
      onRotationEnd={angle => onRotate(id, angle)}
      header={
        focused || showNodeHeaders ? (
          <YardBlockHeader
            id={id}
            name={name}
            maxTiers={maxTiers}
            description={description}
            equipmentTypes={equipmentTypes}
            accessPoints={data.accessPoints ?? []}
            focused={focused}
            isEditable={isEditable}
            hasOccupancies={hasOccupancies}
            isOvercapacity={isOvercapacity}
            isWarning={isWarning}
            utilizationPct={utilizationPct}
            consumedTeu={consumedTeu}
            totalTeu={totalTeu}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddAccessPoint={onAddAccessPoint}
            onEditAccessPoint={onEditAccessPoint}
          />
        ) : undefined
      }
    >
      <FocusWrapper
        focused={focused}
        isEditable={isEditable}
        onFocus={() => onFocus(id)}
        hideGrayBackground
      >
        <Box sx={{ position: 'relative' }}>
          <Stack direction='row'>
            <YardBlockRowHeader
              maxRows={maxRows}
              rowNames={rowNames}
              isEditable={isEditable}
              configAreas={configAreas}
              blockName={showTopLeftCornerCell ? name : undefined}
              onClick={row => onDoubleClick(id, undefined, row)}
              onRenameRow={row => onRenameRow(id, row)}
              onInsertRowUp={row => {
                return onInsertRowUp(id, row)
              }}
              onInsertRowDown={row => {
                return onInsertRowDown(id, row)
              }}
              onDeleteRow={row => {
                return onDeleteRow(id, row)
              }}
              onAdjustMaxTier={row =>
                onAdjustMaxTier(YardBlockEditSectionDialogType.Row, id, undefined, row)
              }
              onBlockConfigArea={async (minBay, maxBay, minRow, maxRow) => {
                await onBlockConfigArea(id, minBay, maxBay, minRow, maxRow)
              }}
              onDeleteConfigArea={onDeleteConfigArea}
            />
            <Stack>
              <YardBlockBayHeader
                isEditable={isEditable}
                maxBays={maxBays}
                bayNames={bayNames}
                configAreas={configAreas}
                reeferConfigAreaByBay={reeferConfigAreaByBay}
                spaceBetweenBaysByEdge={spaceBetweenBaysByEdge}
                onClick={bay => onDoubleClick(id, bay, undefined)}
                onRenameBay={bay => onRenameBay(id, bay)}
                onInsertBayLeft={bay => onInsertBayLeft(id, bay)}
                onInsertBayRight={bay => onInsertBayRight(id, bay)}
                onDeleteBay={bay => onDeleteBay(id, bay)}
                onBlockConfigArea={async (minBay, maxBay, minRow, maxRow) => {
                  await onBlockConfigArea(id, minBay, maxBay, minRow, maxRow)
                }}
                onDeleteConfigArea={onDeleteConfigArea}
                onAddReeferPlugs={async (bay, direction) =>
                  await onAddReeferPlugs(id, bay, direction)
                }
                onAddSpaceBetweenBays={bay => onAddSpaceBetweenBays(id, bay)}
                onDeleteSpaceBetweenBays={async bay => await onDeleteSpaceBetweenBays(id, bay)}
                onAdjustMaxTier={bay =>
                  onAdjustMaxTier(YardBlockEditSectionDialogType.Bay, id, bay)
                }
                bayGaps={bayGaps}
                hasLeadingGap={hasLeadingGap}
                hasTrailingGap={hasTrailingGap}
                hasTopLeftCornerCell={showTopLeftCornerCell}
              />
              <YardBlockSlots
                isEditable={isEditable}
                showAllocationRulesPreview={showAllocationRulesPreview}
                allocationRules={allocationRules}
                selectedAllocationRuleIndex={selectedAllocationRuleIndex}
                blockId={id}
                maxBays={maxBays}
                maxRows={maxRows}
                maxTiers={maxTiers}
                occupancies={occupancies}
                colorScheme={colorScheme}
                selectedSchemeValues={selectedSchemeValues}
                stackConfigArea={stackConfigArea}
                onClick={(bay, row) => onDoubleClick(id, bay, row)}
                onBlockConfigArea={async (minBay, maxBay, minRow, maxRow) => {
                  await onBlockConfigArea(id, minBay, maxBay, minRow, maxRow)
                }}
                onDeleteConfigArea={onDeleteConfigArea}
                onAdjustMaxTier={(bay, row) =>
                  onAdjustMaxTier(YardBlockEditSectionDialogType.Slot, id, bay, row)
                }
                bayGaps={bayGaps}
                hasLeadingGap={hasLeadingGap}
              />
            </Stack>
          </Stack>
          <BlockAccessPointBars
            accessPoints={data.accessPoints ?? []}
            maxBays={maxBays}
            maxRows={maxRows}
            bayGaps={bayGaps}
          />
        </Box>
      </FocusWrapper>
    </RotatableNodeWrapper>
  )
})
